import React, { useState } from "react";
import dayjs from "dayjs";
import { ScrollContainer, StyledTable, Td, Th } from "./TransactionSync.styled";
import { Condition } from "../../Cards/Condition";
import { Langugage } from "../../Cards/Language";
import {
  CardToSyncByUserIdCardType,
  CardToSyncStatusEnum,
} from "magic-shared-types";
import { useAddCommas } from "../../../utils";
import { useSelector } from "react-redux";
import { selectGame } from "../../../store/slices/global";
import { useUpdateSyncStatusMutation } from "../../../services/myServer/CardToSync";
import toast from "react-hot-toast";
import { StatusEditable } from "../StatusEditable/StatusEditable";
import { ScrollStyles } from "../../common/common.style";

interface TransactionCreateCardsProps {
  data: CardToSyncByUserIdCardType[];
  refetch: () => void;
}

export const TransactionSync: React.FC<TransactionCreateCardsProps> = ({
  data,
  refetch,
}) => {
  const [isEditStatus, setIsEditStatus] = useState(false);
  const addCommas = useAddCommas();
  const game = useSelector(selectGame);
  const [updateSyncStatus] = useUpdateSyncStatusMutation();
  const hasFoil = !["yugioh", "onepiece", "pokemon"].includes(
    game.card_trader_game
  );
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);

  const cardStatues = Object.values(CardToSyncStatusEnum);

  const onConfirmNewStatus = async (
    status: CardToSyncStatusEnum,
    id: number
  ) => {
    setIsEditStatus(false);
    try {
      await updateSyncStatus({ syncId: id, status });
      toast.success("Status changed succesfully");
      refetch();
    } catch (e) {
      toast.success("Error during change status");
    }
  };

  return (
    <ScrollContainer>
      <StyledTable>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>Name</Th>
            <Th>Status</Th>
            <Th>Source</Th>
            <Th>Blueprint ID</Th>
            <Th>Card Market ID</Th>
            <Th>Language</Th>
            <Th>Quantity</Th>
            <Th>Condition</Th>
            <Th>Price</Th>
            {hasFoil && <Th>Foil</Th>}
            {hasFirstEdition && <Th>First Edition</Th>}
            {hasReverse && <Th>Reverse</Th>}
            <Th>Signed</Th>
            <Th>Altered</Th>
            <Th>Description</Th>
            <Th>Created At</Th>
            <Th>Error cm</Th>
            <Th>Error ct</Th>
            <Th>Game</Th>
          </tr>
        </thead>
        <tbody>
          {data?.map((card) => (
            <tr key={card.id}>
              <Td>{card.id}</Td>
              <Td>{card.name}</Td>
              <Td>
                <StatusEditable
                  statuses={cardStatues}
                  status={card.status}
                  isEditStatus={isEditStatus}
                  setIsEditStatus={setIsEditStatus}
                  onConfirmNewStatus={(status, id) =>
                    onConfirmNewStatus(status as CardToSyncStatusEnum, id)
                  }
                  id={card.id}
                />
              </Td>
              <Td>{card.source}</Td>
              <Td>
                <a
                  href={`https://www.cardtrader.com/cards/${card.blue_print_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {card.blue_print_id}
                </a>
              </Td>
              <Td>
                {card.card_market_id ? (
                  <a
                    href={`https://www.cardmarket.com/en/${game.card_market_game}/Products?idProduct=${card.card_market_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {card.card_market_id}
                  </a>
                ) : (
                  "-"
                )}
              </Td>
              <Td>
                <Langugage language={card.language} />
              </Td>
              <Td>{card.sold_quantity}</Td>
              <Td>
                <Condition condition={card.condition} />
              </Td>
              <Td>{addCommas(card.price)} €</Td>
              {hasFoil && <Td>{card.is_foil ? "Yes" : "No"}</Td>}
              {hasFirstEdition && (
                <Td>{card.is_first_edition ? "Yes" : "No"}</Td>
              )}
              {hasReverse && <Td>{card.is_reverse ? "Yes" : "No"}</Td>}
              <Td>{card.is_signed ? "Yes" : "No"}</Td>
              <Td>{card.is_altered ? "Yes" : "No"}</Td>
              <Td css={{ width: 220 }}>{card.description || "-"}</Td>
              <Td>{dayjs(card.createdAt).format("DD MM HH:mm:ss")}</Td>
              <Td
                css={{
                  maxWidth: 200,
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  ...ScrollStyles,
                }}
              >
                <div style={{ display: "inline-block", minWidth: "100%" }}>
                  {card.error_cm ? JSON.stringify(card.error_cm, null, 2) : ""}
                </div>
              </Td>
              <Td
                css={{
                  maxWidth: 200,
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  ...ScrollStyles,
                }}
              >
                <div style={{ display: "inline-block", minWidth: "100%" }}>
                  {card.error_ct ? JSON.stringify(card.error_ct, null, 2) : ""}
                </div>
              </Td>
              <Td>{card.game.display_name}</Td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </ScrollContainer>
  );
};
